<template>
  <div class="c-filepond-wrapper">
    <FilePond
      ref="pond"
      name="file"
      :disabled="props.disabled"
      :server="serverConfig"
      :credits="''"
      :accepted-file-types="props.acceptedFileTypes"
      :max-file-size="maxFileSize + 'MB'"
      :label-idle="label"
      :chunk-uploads="true"
      :chunk-force="true"
      :chunk-size="4000000"
      :chunk-retry-delays="[500, 1000, 3000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000, 10000]"
      :label-invalid-field="$t('fileUploads.invalidFile')"
      :label-file-load-error="$t('fileUploads.fileLoadError')"
      :label-file-loading="$t('fileUploads.loading')"
      label-file-processing=""
      :label-file-processing-aborted="$t('fileUploads.uploadCancelled')"
      label-file-processing-complete="100%"
      :label-file-processing-error="$t('fileUploads.uploadFailed')"
      :label-file-processing-revert-error="$t('fileUploads.uploadCancelledError')"
      :label-file-remove-error="$t('fileUploads.deleteFile')"
      :label-file-size-not-available="$t('fileUploads.fileInformationUnavailable')"
      :label-file-type-not-allowed="$t('fileUploads.fileTypeUnsupported')"
      :label-file-waiting-for-size="$t('fileUploads.waitForFileInformation')"
      label-tap-to-undo=""
      label-tap-to-retry=""
      :allow-process="false"
      :allow-replace="false"
      :allow-retry="false"
      :allow-revert="false"
      style-button-remove-item-position="right"
      @processfiles="fileUploaded"
      @processfileprogress="processFileProgress"
      @addfile="fileAdded"
      @removefile="fileRemoved"
      @processfile="onProcessFile"
      @error="handleError" />
    <span class="t-caption1">{{$t('fileUploads.upToMB', { size: maxFileSize })}}</span>
  </div>
</template>

<script setup>
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size/dist/filepond-plugin-file-validate-size.esm.min'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type/dist/filepond-plugin-file-validate-type.esm.min'
import { ref } from 'vue'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'

import api from '@/logic/api'
import { relaunchApi } from '@/logic/relaunchApi'

const props = defineProps({
  name: {
    type: String,
  },
  acceptedFileTypes: {
    type: Array,
  },
  maxFileSize: {
    type: Number,
    default: () => 20,
  },
  disabled: {
    type: Boolean,
  },
})
const emit = defineEmits(['fileUploaded', 'fileAdded', 'fileRemoved', 'error', 'updateUploadingProgress'])

const label = ref(`
  <div>
    <img src="/img/upload.svg" alt="upload-icon"/>
  </div>
  <div class="t-body3 u-marginBottom2">${api.i18n.t('fileUploads.dragItemsHere')}</div>
`)

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize)
const pond = ref()

const defaultHeaders = relaunchApi.getDefaultHeaders()
delete defaultHeaders['Content-Type']

const serverConfig = {
  url: relaunchApi.getURL('/chat/filepond/api'),
  process: {
    url: '/process',
    headers: (file) => {
      if (file.size <= 0) {
        pond.value.removeFile(file)
      }
      return { ...defaultHeaders, 'Upload-Name': file.name, 'Content-Type': file.type }
    },
    withCredentials: false,
  },
  patch: {
    url: '/?patch=',
    headers: defaultHeaders,
    withCredentials: false,
  },
  revert: {
    url: '/process',
    headers: defaultHeaders,
    withCredentials: false,
  },
}

function handleError() {
  emit('error')
}

function configureFilepond() {
  pond.value._pond.setOptions({
    chunkUploads: true,
    server: serverConfig,
  })
}

const fileAdded = (error, file) => {
  const maxFileSize = props.maxFileSize * 1024 * 1024
  if (file.file.size > maxFileSize || error?.main === 'File is too large') {
    pond.value.removeFile(file)
    api.store.commit('notifications/addError', {
      title: api.i18n.t('notifications.max_file_size', { size: props.maxFileSize }),
    })
    return
  }
  configureFilepond()
  emit('fileAdded', file)
}

const onProcessFile = (error, file) => {
  emit('onProcessFile', error, file)
}

const fileRemoved = (_, file) => {
  emit('fileRemoved', file)
}

const fileUploaded = () => {
  const file = pond.value.getFiles()[0]
  emit('fileUploaded', file.serverId, file)
}

const processFileProgress = (file, progress) => {
  emit('updateUploadingProgress', (progress * 100).toFixed(2), file)
}

defineExpose({
  getFilePond() {
    return pond.value
  },
})
</script>

<style lang="scss">
@import '@/assets/scss/config';
.c-filepond--wrapper {
  color: #6c7884;
}
.filepond--root {
  background: white;
  margin-bottom: 0;
  border-radius: $spacing-5;

  &[data-hopper-state='drag-over'] {
    background-color: $color-light-gray2;
  }
}

.filepond--list {
  left: 0;
  right: 0;
}

.filepond--panel-root {
  background-color: transparent;
}

.filepond--item {
  margin: 0;
}

.filepond--item-panel {
  border-radius: 4px;
}

.filepond--drop-label {
  color: #6c7884;
  border: 1px dashed $color-primary;
  border-radius: $spacing-5;
  height: auto;
}

.filepond--drop-label.filepond--drop-label label {
  padding: 33px 5px;
}

.filepond--file {
  align-items: center;
  padding: 8px 0;
  border-radius: 0;

  [data-align*='right'] {
    right: 11px;
  }

  .filepond--file-info {
    height: 100%;
    justify-content: center;
    transform: translate3d(0, 0, 0) !important;
    margin-left: 16px;

    .filepond--file-info-main {
      font-size: 14px;
      font-weight: normal;
    }

    .filepond--file-info-sub {
      display: none;
    }
  }

  .filepond--file-status {
    height: 100%;
    justify-content: center;
    transform: translate3d(0, 0, 0) !important;
    margin-right: 36px;

    .filepond--file-status-main {
      font-size: 12px;
      font-weight: bold;
    }

    .filepond--file-status-sub {
      display: none;
    }
  }

  .filepond--processing-complete-indicator {
    width: 16px;
    height: 16px;
    right: 18px !important;
    margin-top: -11px;
  }
}

.filepond--file-action-button.filepond--file-action-button {
  width: 16px;
  height: 16px;
  background: transparent;
  box-shadow: 0 0 0 0.125em hsla(0, 0%, 100%, 0.9);
}

.filepond--item-panel {
  background: #edeff8;
}

[data-filepond-item-state='processing-complete'] {
  .filepond--item-panel {
    background: $color-primary;
  }
}

[data-filepond-item-state='processing-error'],
[data-filepond-item-state='load-invalid'] {
  .filepond--item-panel {
    background: $color-secondary;
  }
}

.filepond--label-action {
  text-decoration: none;
}

.filepond-browse_btn {
  padding: $spacing-2 $spacing-4;
  border-radius: $spacing-5;
  border: 2px solid $color-primary;
  color: #3d3d53;
  transition: 0.2s background-color ease-in;

  &:hover {
    background-color: $color-light-gray2;
  }
}
</style>
